import { Children, type ReactNode } from 'react';
import { Box, type BoxProps } from '@chakra-ui/react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeHighlight from 'rehype-highlight';
import { parseTimestamp, type SeekMoment } from './api';
import './coach.scss';

// Matches a bracketed timestamp the coach cites in prose/tables: [mm:ss] or [h:mm:ss].
const TS_SPLIT = /(\[\d{1,2}:\d{2}(?::\d{2})?\])/g;
const TS_ONE = /^\[(\d{1,2}:\d{2}(?::\d{2})?)\]$/;

// Replace any [mm:ss] inside a run of text with a clickable token that seeks the recording. Only
// string children carry the timestamps; nested elements (bold/em/links) are handled by their own
// overridden renderer, so this recurses naturally through the markdown tree.
function linkify(children: ReactNode, onSeek: SeekMoment): ReactNode {
  return Children.map(children, (child) => {
    if (typeof child !== 'string' || !child.includes('[')) return child;
    const parts = child.split(TS_SPLIT);
    if (parts.length === 1) return child;
    return parts.map((part, i) => {
      const match = part.match(TS_ONE);
      const seconds = match ? parseTimestamp(match[1]) : null;
      if (match && seconds !== null) {
        return (
          <button
            key={i}
            type="button"
            className="coach-ts-inline"
            onClick={(e) =>
              onSeek(seconds, match[1], e.currentTarget.getBoundingClientRect())
            }
          >
            {part}
          </button>
        );
      }
      return part;
    });
  });
}

// Override the text-bearing elements so timestamps anywhere (prose, lists, table cells, bold runs)
// become seekable. `_node` is dropped — it's react-markdown's AST handle, not a DOM attribute.
function timestampComponents(onSeek: SeekMoment): Components {
  const wrap = (Tag: keyof JSX.IntrinsicElements) => {
    const Wrapped = ({ node: _node, children, ...props }: any) => (
      <Tag {...props}>{linkify(children, onSeek)}</Tag>
    );
    Wrapped.displayName = `CoachMd_${Tag}`;
    return Wrapped;
  };
  return {
    p: wrap('p'),
    li: wrap('li'),
    strong: wrap('strong'),
    em: wrap('em'),
    td: wrap('td'),
    th: wrap('th'),
    h1: wrap('h1'),
    h2: wrap('h2'),
    h3: wrap('h3'),
    h4: wrap('h4'),
  };
}

/**
 * Markdown for the coach copilot. Same engine as PrepMarkdown (react-markdown + remark-gfm +
 * remark-breaks), styled via the `.coach-md` class. When `onSeek` is provided, any [mm:ss] the coach
 * cites becomes a clickable token that opens the moment player — the same player the note's ▶ pills use.
 */
export function CoachMarkdown({
  children,
  onSeek,
  ...boxProps
}: { children: string; onSeek?: SeekMoment } & BoxProps) {
  return (
    <Box className="coach-md" {...boxProps}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[
          [rehypeHighlight, { detect: true, ignoreMissing: true }],
        ]}
        components={onSeek ? timestampComponents(onSeek) : undefined}
      >
        {children || ''}
      </ReactMarkdown>
    </Box>
  );
}

export default CoachMarkdown;
